import { useEffect, useRef } from "react";

const ITEMS = [
  {
    key: "organic",
    title: "100% Organic",
    subtitle: "Grown without synthetic chemicals on certified organic farms",
    image: "/images/why-1.jpeg",
    tag: "Certified",
  },
  {
    key: "chemical",
    title: "Chemical Free",
    subtitle: "No pesticides, no additives — just pure natural produce",
    image: "/images/why-2.jpeg",
    tag: "Pure",
  },
  {
    key: "nutrient",
    title: "Nutrient Rich",
    subtitle: "Packed with vitamins and minerals your body truly needs",
    image: "/images/why-3.jpeg",
    tag: "Wholesome",
  },
  {
    key: "no-preservatives",
    title: "No Preservatives",
    subtitle: "Fresh from farm to your table — nothing artificial added",
    image: "/images/why-5.jpeg",
    tag: "Natural",
  },
  {
    key: "satisfaction",
    title: "Customer Satisfaction",
    subtitle: "Thousands of happy families trust RK Farm Foods daily",
    image: "/images/why-4.jpeg",
    tag: "Trusted",
  },
];

export default function WhyChoose() {
  return (
    <section style={styles.section}>
      {/* Subtle texture overlay */}
      <div style={styles.texture} />

      <div style={styles.inner}>
        <p style={styles.eyebrow}>— The RK Farm Promise —</p>
        <h2 style={styles.title}>Why Choose RK Farm Foods</h2>
        <div style={styles.ornament}>
          <span style={styles.ornLine} />
          <span style={styles.ornDiamond} />
          <span style={styles.ornLine} />
        </div>
        <p style={styles.subtitle}>
          Every product we grow reflects our unwavering commitment to quality,
          purity, and your family's wellbeing.
        </p>

        <div style={styles.grid}>
          {ITEMS.map(({ key, title, subtitle, image, tag }, i) => (
            <Card
              key={key}
              title={title}
              subtitle={subtitle}
              image={image}
              tag={tag}
              delay={0.08 + i * 0.1}
            />
          ))}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Cormorant+SC:wght@400;500;600&display=swap');

        @keyframes cardRise {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .rk-card:hover .rk-photo {
          transform: scale(1.07);
        }
        .rk-card:hover {
          box-shadow: 0 16px 48px rgba(0,0,0,0.55);
          transform: translateY(-4px);
        }
      `}</style>
    </section>
  );
}

function Card({ title, subtitle, image, tag, delay }) {
  return (
    <div
      className="rk-card"
      style={{
        ...styles.card,
        animation: `cardRise 0.75s cubic-bezier(.22,.68,0,1.15) ${delay}s both`,
      }}
    >
      <div style={styles.photoWrap}>
        <img
          src={image}
          alt={title}
          className="rk-photo"
          style={styles.photo}
          loading="lazy"
        />
        <div style={styles.photoOverlay} />
        {/* Tag pill */}
        <span style={styles.tag}>{tag}</span>
      </div>

      <div style={styles.cardBody}>
        {/* Thin top rule */}
        <div style={styles.rule} />
        <h3 style={styles.cardTitle}>{title}</h3>
        <p style={styles.cardSub}>{subtitle}</p>
        {/* Bottom flourish */}
        <div style={styles.flourish}>❧</div>
      </div>
    </div>
  );
}

const styles = {
  section: {
    position: "relative",
    background: "#0c1f0a",
    padding: "80px 36px 88px",
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  texture: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 20% 50%, rgba(45,122,28,0.08) 0%, transparent 60%)," +
      "radial-gradient(ellipse at 80% 30%, rgba(74,173,46,0.06) 0%, transparent 55%)",
    pointerEvents: "none",
  },
  inner: {
    position: "relative",
    zIndex: 2,
    maxWidth: 1120,
    margin: "0 auto",
  },

  // ── Heading block ──
  eyebrow: {
    fontFamily: "'Cormorant SC', Georgia, serif",
    fontSize: "0.78rem",
    fontWeight: 500,
    letterSpacing: "4px",
    color: "#7ec850",
    margin: "0 0 16px",
    textTransform: "uppercase",
  },
  title: {
    fontFamily: "'Cinzel', 'Times New Roman', serif",
    fontSize: "clamp(1.7rem, 3vw, 2.6rem)",
    fontWeight: 700,
    color: "#f0ede6",
    margin: "0 0 20px",
    letterSpacing: "1.5px",
    lineHeight: 1.2,
  },
  ornament: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    margin: "0 auto 22px",
    width: 160,
  },
  ornLine: {
    flex: 1,
    height: 1,
    background: "rgba(126,200,80,0.45)",
    display: "block",
  },
  ornDiamond: {
    display: "block",
    width: 7,
    height: 7,
    background: "#4aad2e",
    transform: "rotate(45deg)",
    flexShrink: 0,
  },
  subtitle: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: "1.08rem",
    fontStyle: "italic",
    color: "rgba(240,237,230,0.62)",
    maxWidth: 560,
    margin: "0 auto 60px",
    lineHeight: 1.8,
    fontWeight: 400,
  },

  // ── Grid ──
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))",
    gap: 22,
    alignItems: "start",
  },

  // ── Card ──
  card: {
    background: "#111f0f",
    border: "1px solid rgba(126,200,80,0.15)",
    borderRadius: 4,
    overflow: "hidden",
    textAlign: "left",
    cursor: "default",
    transition: "transform 0.32s ease, box-shadow 0.32s ease",
    boxShadow: "0 6px 28px rgba(0,0,0,0.4)",
  },
  photoWrap: {
    position: "relative",
    width: "100%",
    paddingTop: "72%",
    overflow: "hidden",
  },
  photo: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.55s ease",
  },
  photoOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to top, rgba(8,18,6,0.72) 0%, rgba(8,18,6,0.1) 55%, transparent 100%)",
    pointerEvents: "none",
  },
  tag: {
    position: "absolute",
    top: 12,
    left: 12,
    fontFamily: "'Cinzel', 'Times New Roman', serif",
    fontSize: "0.6rem",
    fontWeight: 600,
    letterSpacing: "2.5px",
    textTransform: "uppercase",
    color: "#f0ede6",
    background: "rgba(30,92,18,0.82)",
    border: "1px solid rgba(126,200,80,0.4)",
    padding: "4px 10px",
    borderRadius: 2,
  },
  cardBody: {
    padding: "18px 20px 20px",
  },
  rule: {
    width: "100%",
    height: 1,
    background: "rgba(126,200,80,0.18)",
    marginBottom: 14,
  },
  cardTitle: {
    fontFamily: "'Cinzel', 'Times New Roman', serif",
    fontSize: "0.88rem",
    fontWeight: 600,
    letterSpacing: "1.2px",
    color: "#f0ede6",
    margin: "0 0 9px",
    lineHeight: 1.35,
    textTransform: "uppercase",
  },
  cardSub: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: "0.95rem",
    fontStyle: "italic",
    color: "rgba(240,237,230,0.55)",
    margin: "0 0 14px",
    lineHeight: 1.65,
    fontWeight: 400,
  },
  flourish: {
    fontFamily: "Georgia, serif",
    fontSize: "1rem",
    color: "rgba(59, 113, 26, 0.4)",
    lineHeight: 1,
    display: "block",
  },
};